import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Loader2 } from 'lucide-react';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { formatDate } from '@/lib/dateFormat';

// ────────────────────────────────────────────────────────────────────────────
// Super-admin notification feed — the header bell. Polls the unread count so
// the badge stays fresh, and opens a dropdown listing recent platform events
// (new signups, "Talk to Sales" inquiries, …). Clicking an item marks it read
// and navigates to its in-app link. Backed by /super-admin/notifications*.
// ────────────────────────────────────────────────────────────────────────────

interface PlatformNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  isRead: boolean;
  createdAt: string;
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso); // project standard "5 Aug 2026" (viewer's local tz)
}

const POLL_MS = 60_000;

export function SuperAdminNotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PlatformNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await saApi.get('/super-admin/notifications/unread-count');
      setUnread(data?.unread ?? 0);
    } catch {
      /* silent — badge is best-effort */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await saApi.get('/super-admin/notifications', { params: { limit: 30 } });
      setItems(data?.items ?? []);
      setUnread(data?.unread ?? 0);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll the unread count while mounted.
  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, [loadCount]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const openItem = (n: PlatformNotification) => {
    if (!n.isRead) {
      setItems(prev => prev.map(x => (x.id === n.id ? { ...x, isRead: true } : x)));
      setUnread(u => Math.max(0, u - 1));
      saApi.patch(`/super-admin/notifications/${n.id}/read`).catch(() => {});
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    setItems(prev => prev.map(x => ({ ...x, isRead: true })));
    setUnread(0);
    try {
      await saApi.post('/super-admin/notifications/read-all');
    } catch {
      /* silent */
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        aria-label="Notifications"
        className="relative text-[#64748B] hover:text-[#6366F1] transition-colors p-1.5 rounded-lg hover:bg-gray-50"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] bg-white rounded-xl border border-slate-200 shadow-2xl z-50 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="text-sm font-bold text-slate-800">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-[#6366F1] font-semibold hover:underline">
                Mark all read
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" />
              </div>
            ) : items.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10">No notifications yet</p>
            ) : (
              items.map(n => (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 flex gap-3 ${
                    n.isRead ? '' : 'bg-indigo-50/40'
                  }`}
                >
                  <span
                    className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${
                      n.isRead ? 'bg-transparent' : 'bg-[#6366F1]'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate">{n.title}</p>
                    <p
                      className="text-xs text-slate-500"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {n.message}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.createdAt)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
